import type { Section } from "../geometry/section";
import type { Pt } from "../math/polygon";

/**
 * Gerçek arka yüz (kademe) yöntemi için geometri: arka sınır zinciri, yükselen kenarlar (yüzler) ve
 * yatay çıkıntılar (kademe üstleri) olarak ayrılır. Yüzler üstten alta sıralanır (1 = tepe).
 */
export interface BackFace {
  index: number;                 // 1'den, üstten alta
  bottom: Pt; top: Pt;
  alpha: number;                 // rad, düşeyden; + → yüz dolguya doğru yatık (dolgu üstüne oturur)
  psi: number;                   // rad, yatayla açı = π/2 − α (TBDY ψ)
  yC: number;                    // yüz doğrusunun dolgu yüzeyiyle kesiştiği kot (derinlik sıfırı)
  xAt: (y: number) => number;    // yüz doğrusu üzerinde x
}
/** Kademe çıkıntısı: y kotunda xIn (üst yüzün dibi) → xOut (alt yüzün tepesi) arası yatay kenar; üstündeki dolgu düşey yük. */
export interface Ledge { index: number; y: number; xIn: number; xOut: number }

const TOL = 1e-9;

export function backFaces(section: Section, tanBeta: number): { faces: BackFace[]; ledges: Ledge[] } {
  const chain = section.backChain;
  const faces: BackFace[] = [];
  const ledges: Ledge[] = [];
  for (let i = 0; i + 1 < chain.length; i++) {
    const a = chain[i]!, b = chain[i + 1]!;
    const dy = b.y - a.y;
    if (dy > TOL) {
      const alpha = Math.atan((a.x - b.x) / dy);
      const tanA = Math.tan(alpha);
      const xAt = (y: number): number => a.x - (y - a.y) * tanA;
      // Yüz doğrusu ∩ dolgu yüzeyi: y = yTop + (x − x_tb)·tanβ, x = a.x − (y − a.y)·tanα
      const denom = 1 + tanA * tanBeta;
      if (denom < 0.05) throw new Error("Dolgu şevi ile kademe arka yüzü kesişmiyor (β çok büyük veya arka yüz çok sarkık)");
      const yC = (section.yTop + (a.x + a.y * tanA - section.topBack.x) * tanBeta) / denom;
      faces.push({ index: 0, bottom: a, top: b, alpha, psi: Math.PI / 2 - alpha, yC: Math.max(yC, b.y), xAt });
    } else if (Math.abs(dy) <= TOL && a.x - b.x > TOL) {
      ledges.push({ index: 0, y: a.y, xIn: b.x, xOut: a.x });
    }
    // aşağı inen ya da sağa açılan (alt yüzü dolguya bakan) kenarlar: itki/ağırlık katkısı yok
  }
  faces.reverse();
  faces.forEach((f, k) => { f.index = k + 1; });
  ledges.reverse();
  ledges.forEach((l, k) => { l.index = k + 1; });
  return { faces, ledges };
}
